import { chatDataType, IChat, IChatMessage, IDocument, messageStatusType } from 'interfaces';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IRepliedMessage } from 'interfaces/IChatMessage';
import dayjs from 'dayjs';
import fetchChatData, {
  createChat,
  createGLobalChat,
  fetchAllTagsDocuments,
  fetchChatHistory,
  fetchChatId,
  fetchChatInfo,
  fetchGlobalChats,
  setRejectMessages,
  splitToGroup,
} from 'store/reducers/chatCreator';
import { sortChatsByLastMessage } from 'helpers/sortChats';

type chatStatusType = 'pending' | 'send';

type chatMessageType = {
  message: string;
  attachment_ids: string[] | null;
  id: string;
  status: messageStatusType;
  replied_to?: IRepliedMessage | null;
};

type initialStateType = {
  displayChatDragger: boolean;
  chatData: chatDataType;
  chatStatus: chatStatusType;
  chatMessage: chatMessageType;
  roomAllChats: IChat[];
  globalChats: IChat[];
  selectedChat: IChat | null;
  isLoadedSession: boolean;
  roomChatLoading: boolean;
  globalChatLoading: boolean;
  currnetChatLoading: boolean;
  isOpenCreateModal: boolean;
  listStatus: 'main' | 'groupList' | 'room';
  checkingMessages: IChatMessage[];
  viewsMessagesIds: string[];
  attemptCount: number;

  messageForReply: IRepliedMessage | null;
  allTagsDocuments: IDocument[];
  isLoadingTagsDocuments: boolean;
  
  isLoadingTagFile: boolean;
  chatsAbortController: AbortController | null;
  
  isOpenDocsMenu: boolean;
  isOpenUsersMenu: boolean;
  isAttachmentModalOpen: boolean;
  messageContent: string;
};

const initialState: initialStateType = {
  displayChatDragger: false,
  chatData: {},
  chatStatus: 'pending',
  chatMessage: {
    id: '',
    status: 'pending',
    message: '',
    replied_to: null,
    attachment_ids: null,
  },
  selectedChat: null,
  roomAllChats: [],
  globalChats: [],
  checkingMessages: [],
  isLoadedSession: false,
  currnetChatLoading: false,
  roomChatLoading: false,
  globalChatLoading: false,
  isOpenCreateModal: false,
  viewsMessagesIds: [],
  attemptCount: 0,
  listStatus: 'room',

  messageForReply: null,
  allTagsDocuments: [],
  isLoadingTagsDocuments: false,
  
  isLoadingTagFile: false,
  chatsAbortController: null,
  
  isOpenDocsMenu: false,
  isOpenUsersMenu: false,
  isAttachmentModalOpen: false,
  messageContent: '',
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setDisplayChatDragger: (state, action: PayloadAction<boolean>) => {
      state.displayChatDragger = action.payload;
    },
    setChatData: (state, action: PayloadAction<chatDataType>) => {
      state.chatData = action.payload;
    },

    setChatStatus: (state, action: PayloadAction<chatStatusType>) => {
      state.chatStatus = action.payload;
    },

    setChatMessage: (state, action: PayloadAction<chatMessageType>) => {
      state.chatMessage = action.payload;
    },

    setIsLoadedSession: (state, action: PayloadAction<boolean>) => {
      state.isLoadedSession = action.payload;
    },

    setAttemptCount: (state, action: PayloadAction<number>) => {
      state.attemptCount = action.payload;
    },

    setCheckingMessages: (state, action: PayloadAction<IChatMessage[]>) => {
      state.checkingMessages = action.payload;
    },

    setSelectedChatRoom: (state, action: PayloadAction<IChat | null>) => {
      state.selectedChat = action.payload
        ? { ...action.payload, unviewed_amount: 0 }
        : action.payload;
      if (action.payload) {
        state.roomAllChats = state.roomAllChats.map((chat) =>
          chat.id === action.payload?.id
            ? { ...chat, unviewed_amount: 0 }
            : chat
        );
        state.globalChats = state.globalChats.map((chat) =>
          chat.id === action.payload?.id
            ? { ...chat, unviewed_amount: 0 }
            : chat
        );
      }
    },

    setUnviewedAmount: (
      state,
      action: PayloadAction<{ count: number; chat_id: string }>
    ) => {
      state.roomAllChats = state.roomAllChats.map((chat) =>
        chat.id === action.payload.chat_id
          ? {
              ...chat,
              unviewed_amount:
                (action.payload.count < 0
                  ? -chat.unviewed_amount
                  : action.payload.count) + chat.unviewed_amount,
            }
          : chat
      );
      state.globalChats = state.globalChats.map((chat) =>
        chat.id === action.payload.chat_id
          ? {
              ...chat,
              unviewed_amount:
                (action.payload.count < 0
                  ? -chat.unviewed_amount
                  : action.payload.count) + chat.unviewed_amount,
            }
          : chat
      );
    },

    setCurrentChatLoading: (state, action: PayloadAction<boolean>) => {
      state.currnetChatLoading = action.payload;
    },

    setRoomAllChats: (state, action: PayloadAction<IChat[]>) => {
      const chatsStore: chatDataType = {};

      action.payload.forEach((el, idx) => {
        chatsStore[el.id] = state.chatData[el.id];
      });

      state.roomAllChats = action.payload;
      state.chatData = chatsStore;
    },

    setGlobalChats: (state, action: PayloadAction<IChat[]>) => {
      state.globalChats = action.payload;
    },

    clearAllChatData: (state) => {
      state.roomAllChats = [];
      state.chatData = {};
      state.selectedChat = null;
    },

    resetChatState: () => initialState,

    setUploadChatsController(
      state,
      action: PayloadAction<AbortController | null>
    ) {
      if (action.payload) {
        state.chatsAbortController = action.payload;
      } else {
        state.chatsAbortController = null;
      }
    },

    setIsOpenCreateModal: (state, action) => {
      state.isOpenCreateModal = action.payload;
    },

    setListStatus: (
      state,
      action: PayloadAction<'main' | 'groupList' | 'room'>
    ) => {
      state.listStatus = action.payload;
    },

    setViewsMessagesIds: (state, action: PayloadAction<string[]>) => {
      state.viewsMessagesIds = action.payload;
    },
    setMessageForReply: (state, action: PayloadAction<any | null>) => {
      state.messageForReply = action.payload;
    },
    setAllTagsDocuments: (state, action: PayloadAction<IDocument[]>) => {
      state.allTagsDocuments = action.payload;
    },

    setIsLoadingTagFile: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTagFile = action.payload;
    },

    setIsOpenDocsMenu: (state, action: PayloadAction<boolean>) => {
      state.isOpenDocsMenu = action.payload;
    },

    setIsOpenUsersMenu: (state, action: PayloadAction<boolean>) => {
      state.isOpenUsersMenu = action.payload;
    },

    setIsAttachmentModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAttachmentModalOpen = action.payload;
    },

    setMessageContent: (state, action: PayloadAction<string>) => {
      state.messageContent = action.payload;
    },

    updateChatLastMessage: (
      state,
      action: PayloadAction<{
        chat_id: string;
        id: string;
        content: string;
        created_at: string;
        status: messageStatusType;
        author: { id: string; name: string; email: string };
        attachment_ids: any;
      }>
    ) => {      
      const updatedRoomChats = state.roomAllChats.map((chat) =>
        chat.id === action.payload.chat_id
          ? {
              ...chat,
              last_message: {
                ...chat.last_message!,
                id: action.payload.id,
                content: action.payload.content,
                author: action.payload.author,
                created_at: action.payload.created_at,
                attachment_ids: action.payload.attachment_ids,
                status: action.payload.status,
              },
            }
          : chat
      );
      state.roomAllChats = sortChatsByLastMessage(updatedRoomChats);
      const updatedGlobalChats = state.globalChats.map((chat) =>
        chat.id === action.payload.chat_id
          ? {
              ...chat,
              last_message: {
                ...chat.last_message!,
                id: action.payload.id,
                content: action.payload.content,
                author: action.payload.author,
                created_at: action.payload.created_at,
                attachment_ids: action.payload.attachment_ids,
                status: action.payload.status,
              },
            }
          : chat
      );
      state.globalChats = sortChatsByLastMessage(updatedGlobalChats);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchChatData.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: IChatMessage[];
          grouped: IChatMessage[][];
          chatId: string;
        }>
      ) => {

        const currentChatData = state.chatData[action.payload.chatId];
        const previousDataLength = currentChatData?.data?.length || 0;
        const newDataLength = action.payload.data.length;
        
        state.chatData = {
          ...state.chatData,
          [action.payload.chatId]: {
            data: action.payload.data,
            grouped: action.payload.grouped,
            isLoadedHistory: state.chatData[action.payload.chatId]
              ? state.chatData[action.payload.chatId].isLoadedHistory
              : false,
            currentPage: currentChatData?.currentPage,
            hasMore: currentChatData?.hasMore,
            totalMessages: newDataLength > previousDataLength 
              ? (currentChatData?.totalMessages || 0) + (newDataLength - previousDataLength)
              : currentChatData?.totalMessages,
          },
        };
      }
    );

    builder.addCase(createChat.pending, (state) => {
      state.currnetChatLoading = true;
    });

    builder.addCase(createChat.rejected, (state) => {
      state.currnetChatLoading = false;
    });

    builder.addCase(
      createChat.fulfilled,
      (state, action: PayloadAction<{ chat: IChat; isForPush: boolean } | undefined>) => {
        if (action.payload) {
          const isExist = state.roomAllChats.find(chat => chat.id === action.payload?.chat?.id);
          const targetChatId = isExist && JSON.parse(JSON.stringify(isExist))?.id;
          action.payload.isForPush && !targetChatId &&
            state.roomAllChats.push(action.payload.chat);
          
          state.selectedChat = action.payload.chat;
          state.currnetChatLoading = false;
          if (
            !state.chatData[action.payload.chat?.id] ||
            !state.chatData[action.payload.chat?.id]?.isLoadedHistory
          ) {
            state.chatData[action.payload.chat.id] = {
              data: [],
              grouped: [],
              isLoadedHistory: false,
              totalMessages: 0,
            };
          }
        }
      }
    );

    builder.addCase(createGLobalChat.pending, (state) => {
      state.globalChatLoading = true;
    });

    builder.addCase(createGLobalChat.rejected, (state) => {
      state.globalChatLoading = false;
    });

    builder.addCase(
      createGLobalChat.fulfilled,
      (state, action: PayloadAction<{ chat: IChat; isForPush: boolean }>) => {
        action.payload.isForPush && state.globalChats.push(action.payload.chat);
        state.selectedChat = action.payload.chat;
        state.globalChatLoading = false;

        if (
          !state.chatData[action.payload.chat?.id] ||
          !state.chatData[action.payload.chat?.id]?.isLoadedHistory
        ) {
          state.chatData[action.payload.chat.id] = {
            data: [],
            grouped: [],
            isLoadedHistory: false,
            totalMessages: 0,
          };
        }
      }
    );

    builder.addCase(fetchChatId.pending, (state) => {
      state.roomChatLoading = true;
    });

    builder.addCase(
      fetchChatId.fulfilled,
      (
        state,
        action: PayloadAction<{
          chats: IChat[];
          selectedChat: IChat | null;
        }>
      ) => {
        const chatsStore: chatDataType = { ...state.chatData };
        state.roomAllChats = action.payload.chats;        
        state.roomChatLoading = false;

        if (!action.payload.selectedChat) return;

        const allChatIds = Object.keys(chatsStore);
        action.payload.chats.forEach((el, idx) => {
          if (el.id === action.payload.selectedChat?.id) return;
          if (allChatIds.includes(el.id)) return;
          
          chatsStore[el.id] = {
            data: [],
            grouped: [],
            isLoadedHistory: false,
            totalMessages: 0,
          };
        });        
        state.chatData = chatsStore;
      }
    );

    builder.addCase(fetchChatId.rejected, (state) => {
      state.roomChatLoading = false;
    });

    builder.addCase(fetchGlobalChats.pending, (state) => {
      state.globalChatLoading = true;
    });

    builder.addCase(
      fetchGlobalChats.fulfilled,
      (
        state,
        action: PayloadAction<{
          chats: IChat[];
          selectedChat: IChat | null;
          checkingMessages: IChatMessage[];
        }>
      ) => {
        const chatsStore: chatDataType = { ...state.chatData };
        state.checkingMessages = action.payload.checkingMessages;

        action.payload.chats.forEach((el, idx) => {
          if (el.id === action.payload.selectedChat?.id) return;
          if (chatsStore[el.id]?.data?.length) return;

          chatsStore[el.id] = {
            data: [],
            grouped: [],
            isLoadedHistory: false,
            totalMessages: 0,
          };
        });

        state.globalChats = action.payload.chats;
        state.globalChatLoading = false;
        state.chatData = chatsStore;
      }
    );

    builder.addCase(fetchGlobalChats.rejected, (state) => {
      state.globalChatLoading = false;
    });

    builder.addCase(fetchChatHistory.pending, (state, action) => {
      if(!action.meta.arg.append){
        state.currnetChatLoading = true;
      }
    });

    builder.addCase(
      fetchChatHistory.fulfilled,
      (
        state,
        action: PayloadAction<{
          chat_id: string;
          history: IChatMessage[];
          splittedHistory: IChatMessage[][];
          hasMore: boolean;
          currentPage?: number;
          append?: boolean;
          totalMessages: number;
        }>
      ) => {
        const { chat_id, history, splittedHistory, hasMore, currentPage, append, totalMessages  } = action.payload;
        
        // Защита от race condition
        // Исключение: id='0'
        if (!append && state.selectedChat?.id !== chat_id) {
          state.chatData[chat_id] = {
            data: history,
            grouped: splittedHistory,
            isLoadedHistory: true,
            currentPage,
            hasMore,
            totalMessages,
          };
          state.currnetChatLoading = false;
          return;
          // Убрал так как наблюдалось неккоректное поведение. Описание: http://10.1.3.25/issues/21793
          // if(state.selectedChat?.id === '0') {
          //   state.chatData[chat_id] = {
          //     data: history,
          //     grouped: splittedHistory,
          //     isLoadedHistory: true,
          //     currentPage,
          //     hasMore,
          //     totalMessages,
          //   };
          //   state.currnetChatLoading = false;
          //   return;
          // } else {
          //   state.currnetChatLoading = false;
          //   return;
          // }
        };
        if (append) {
          const allMessages = [...history, ...state.chatData[chat_id].data]
            .sort((a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix());
        
          state.chatData[chat_id].data = allMessages;
          state.chatData[chat_id].grouped = splitToGroup(allMessages);
          state.chatData[chat_id].currentPage = currentPage;
          state.chatData[chat_id].hasMore = hasMore;
          state.chatData[chat_id].totalMessages = totalMessages;
        } else {
          state.chatData[chat_id] = {
            data: history,
            grouped: splittedHistory,
            isLoadedHistory: true,
            currentPage,
            hasMore,
            totalMessages,
          };
          state.currnetChatLoading = false;
        }
      }
    );

    builder.addCase(fetchChatHistory.rejected, (state, action) => {
      state.currnetChatLoading = false;
    });

    builder.addCase(
      fetchChatInfo.fulfilled,
      (state, action: PayloadAction<IChat>) => {
        !state.chatData[action.payload.id] &&
          state.roomAllChats.push(action.payload);
        
        state.chatData[action.payload.id] = {
          grouped: [],
          data: [],
          isLoadedHistory: false,
          totalMessages: 0,
        };
      }
    );

    builder.addCase(
      setRejectMessages.fulfilled,
      (
        state,
        action: PayloadAction<{
          chatData: chatDataType;
          allRejectedMessages: string[][];
          rejectedMessages: IChatMessage[];
          isShowSupport: boolean;
        }>
      ) => {
        const {
          allRejectedMessages: [roomRejectedMessages, mainRejectedMessages],
        } = action.payload;
        state.chatData = action.payload.chatData;
        state.checkingMessages = action.payload.rejectedMessages;

        state.roomAllChats = state.roomAllChats.map((chat) =>
          roomRejectedMessages.includes(chat.id)
            ? { ...chat, rejected_amount: true }
            : chat
        );

        if (mainRejectedMessages.length) {
          state.globalChats = state.globalChats.map((chat) =>
            mainRejectedMessages.includes(chat.id)
              ? { ...chat, rejected_amount: true }
              : chat
          );
        }
      }
    );

    builder.addCase(fetchAllTagsDocuments.pending, (state) => {
      state.isLoadingTagsDocuments = true;
    });
    builder.addCase(fetchAllTagsDocuments.rejected, (state) => {
      state.isLoadingTagsDocuments = false;
    });
    builder.addCase(fetchAllTagsDocuments.fulfilled, (state, action: PayloadAction<IDocument[]>) => {
      state.isLoadingTagsDocuments = false;
      state.allTagsDocuments = action.payload;
    });
  },
});

const chatReducer = chatSlice.reducer;
export const {
  setDisplayChatDragger,
  setChatData,
  setChatStatus,
  setChatMessage,
  setIsLoadedSession,
  setSelectedChatRoom,
  setCurrentChatLoading,
  clearAllChatData,
  setRoomAllChats,
  setGlobalChats,
  setIsOpenCreateModal,
  setUnviewedAmount,
  setListStatus,
  setCheckingMessages,
  setViewsMessagesIds,
  setAttemptCount,
  setMessageForReply,
  updateChatLastMessage,
  setAllTagsDocuments,
  setIsLoadingTagFile,
  resetChatState,
  setUploadChatsController,

  setIsOpenDocsMenu,
  setIsOpenUsersMenu,
  setIsAttachmentModalOpen,
  setMessageContent,
} = chatSlice.actions;
export default chatReducer;
